import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigation } from '../../../contexts/NavigationContext';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import logo from '../../../../assets/images/logo.jpeg';
import { homeLogoPath } from '../../../utils/hubNavigation';
import { hasActiveSubscription } from '../../../utils/subscriptionGate';

const buildNavTabs = (authenticated, user, fitnessHubsOnly, activeFitnessProfile, subscription) => {
	const tabs = [];

	if (authenticated) {
		const hubChildren = [
			{
				id: 'front-office',
				label: 'Front Office',
				icon: 'fa-building',
				path: '/front-office'
			},
			{
				id: 'best-ball-zone',
				label: 'Best Ball Zone',
				icon: 'fa-layer-group',
				path: '/best-ball-zone'
			},
			{
				id: 'classroom',
				label: 'The Classroom',
				icon: 'fa-chalkboard',
				path: '/classroom'
			},
			{
				id: 'fitness-hub',
				label: 'Fitness Hub',
				icon: 'fa-dumbbell',
				path: '/fitness-hub'
			},
			{
				id: 'nutrition-lab',
				label: 'Nutrition Lab',
				icon: 'fa-apple-whole',
				path: '/nutrition-lab'
			}
		];

		const visibleHubChildren = fitnessHubsOnly
			? hubChildren.filter((hub) => ['fitness-hub', 'nutrition-lab'].includes(hub.id))
			: hubChildren;

		tabs.push({
			id: 'hubs',
			label: 'Hubs',
			icon: 'fa-th-large',
			isSection: true,
			children: visibleHubChildren
		});
	}

	if (!fitnessHubsOnly) {
		const exploreChildren = [
			{
				id: 'news',
				label: 'News Center',
				icon: 'fa-newspaper',
				path: '/news'
			},
			{
				id: 'players',
				label: 'NFL Players',
				icon: 'fa-people-group',
				path: '/players'
			},
			{
				id: 'teams',
				label: 'NFL Teams',
				icon: 'fa-football',
				path: '/teams'
			},
			...(authenticated
				? [
						{
							id: 'injury-report',
							label: 'Injury Report',
							icon: 'fa-user-injured',
							path: '/injury-report'
						}
					]
				: []),
			...(authenticated && hasActiveSubscription(subscription)
				? [
						{
							id: 'player-rankings',
							label: 'Player Rankings',
							icon: 'fa-list-ol',
							path: '/player-rankings'
						},
						{
							id: 'eliminator-player-matchups',
							label: 'Elim Player Rankings',
							icon: 'fa-th',
							path: '/eliminator-player-matchups'
						}
					]
				: []),
			{
				id: 'prospects',
				label: 'College Prospects',
				icon: 'fa-binoculars',
				path: '/prospects'
			},
			{
				id: 'glossary',
				label: 'Glossary',
				icon: 'fa-book',
				path: '/glossary'
			}
		];

		tabs.push({
			id: 'explore',
			label: 'Explore',
			icon: 'fa-compass',
			isSection: true,
			children: exploreChildren
		});
	}

	if (authenticated && !fitnessHubsOnly) {
		tabs.push(
			{
				id: 'dashboard',
				label: 'Dashboard',
				icon: 'fa-tachometer-alt',
				path: '/dashboard'
			},
			{
				id: 'rosters',
				label: 'My Rosters',
				icon: 'fa-clipboard-list',
				path: '/rosters'
			}
		);
	}

	if (authenticated && !activeFitnessProfile) {
		tabs.push({
			id: 'connections',
			label: 'Connections',
			icon: 'fa-people-arrows',
			isSection: true,
			children: [
				{
					id: 'friends',
					label: 'Friends',
					icon: 'fa-user-group',
					path: '/friends'
				},
				{
					id: 'linked-households',
					label: 'Households',
					icon: 'fa-house-chimney-user',
					path: '/linked-households'
				}
			]
		});
	}

	return tabs;
};

const AccordionNavigation = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, authenticated, signOut, activeFitnessProfile, fitnessHubsOnly, subscription } = useAuth();
	const { isAccordionOpen, closeAccordion } = useNavigation();
	const [
		expandedSections,
		setExpandedSections
	] = useState({});
	const sidebarRef = useRef(null);

	const tabs = useMemo(
		() => buildNavTabs(authenticated, user, fitnessHubsOnly, activeFitnessProfile, subscription),
		[
			authenticated,
			user,
			fitnessHubsOnly,
			activeFitnessProfile,
			subscription
		]
	);

	const logoPath = homeLogoPath({ authenticated, user, fitnessHubsOnly });

	useEffect(
		() => {
			const handleClickOutside = (event) => {
				if (
					sidebarRef.current &&
					!sidebarRef.current.contains(event.target) &&
					isAccordionOpen
				) {
					closeAccordion();
				}
			};

			if (isAccordionOpen) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		},
		[
			isAccordionOpen,
			closeAccordion
		]
	);

	useEffect(
		() => {
			closeAccordion();
		},
		[
			location.pathname,
			closeAccordion
		]
	);

	useEffect(
		() => {
			const autoExpanded = {};
			tabs.forEach((tab) => {
				if (
					tab.isSection &&
					tab.children &&
					tab.children.some((child) => location.pathname === child.path)
				) {
					autoExpanded[tab.id] = true;
				}
			});
			if (Object.keys(autoExpanded).length > 0) {
				setExpandedSections((prev) => ({ ...prev, ...autoExpanded }));
			}
		},
		[
			location.pathname,
			tabs
		]
	);

	const toggleSection = (sectionId) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId]
		}));
	};

	const handleNavigate = (path) => {
		navigate(path);
		closeAccordion();
	};

	const renderNavItem = (item, isChild = false) => {
		const isActive = location.pathname === item.path;

		if (item.isSection) {
			const isExpanded = expandedSections[item.id];
			const hasActiveChild =
				item.children &&
				item.children.some((child) => location.pathname === child.path);

			return (
				<div key={item.id} className="mb-1">
					<button
						type="button"
						onClick={() => toggleSection(item.id)}
						className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${hasActiveChild
							? 'bg-gray-100 dark:bg-dark-border text-ff-black dark:text-dark-text font-medium'
							: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-border'}`}
					>
						<span className="flex items-center gap-3">
							<i className={`fa-solid ${item.icon} w-5 text-center`} />
							<span className="font-medium">{item.label}</span>
						</span>
						<i
							className="fa-solid fa-chevron-right text-sm transition-transform duration-200"
							style={{
								transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
							}}
						/>
					</button>
					<div
						className="overflow-hidden transition-all duration-300 ease-in-out"
						style={{
							maxHeight: isExpanded ? '500px' : '0',
							opacity: isExpanded ? 1 : 0,
							marginTop: isExpanded ? '0.25rem' : 0
						}}
					>
						<div className="ml-4">
							{item.children &&
								item.children.map((child) => renderNavItem(child, true))}
						</div>
					</div>
				</div>
			);
		}

		return (
			<button
				key={item.id}
				type="button"
				onClick={() => handleNavigate(item.path)}
				className={`w-full flex items-center gap-3 rounded-lg transition-all duration-200 mb-1 ${isChild ? 'pl-8 pr-4' : 'px-4'} py-3 ${isActive
					? 'bg-ff-black text-white shadow-md font-medium'
					: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-border'}`}
			>
				<i className={`fa-solid ${item.icon} w-5 text-center flex-shrink-0`} />
				<span className="font-medium">{item.label}</span>
			</button>
		);
	};

	const isHouseholdSession = Boolean(activeFitnessProfile?.id);

	const displayName = useMemo(() => {
		if (activeFitnessProfile?.name) {
			return activeFitnessProfile.name;
		}

		if (user && (user.first_name || user.last_name)) {
			return `${user.first_name || ''} ${user.last_name || ''}`.trim();
		}

		return user?.email || 'Account';
	}, [
		activeFitnessProfile,
		user
	]);

	return (
		<React.Fragment>
			<div
				role="presentation"
				onClick={closeAccordion}
				className={`fixed inset-0 z-40 transition-opacity duration-300 ${isAccordionOpen
					? 'opacity-100 pointer-events-auto'
					: 'opacity-0 pointer-events-none -z-10'}`}
				style={{
					backgroundColor: isAccordionOpen
						? 'rgba(0, 0, 0, 0.5)'
						: 'transparent',
					backdropFilter: isAccordionOpen ? 'blur(4px)' : 'none',
					WebkitBackdropFilter: isAccordionOpen ? 'blur(4px)' : 'none'
				}}
			/>

			<aside
				ref={sidebarRef}
				className={`fixed left-0 top-0 h-full w-80 z-50 bg-white dark:bg-dark-card shadow-2xl border-r border-gray-200 dark:border-dark-border transform transition-transform duration-300 ease-out ${isAccordionOpen ? 'translate-x-0' : '-translate-x-full'}`}
				aria-hidden={!isAccordionOpen}
			>
				<div className="flex flex-col h-full">
					<div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-dark-border">
						<Link
							to={logoPath}
							onClick={closeAccordion}
							className="flex items-center gap-3 min-w-0"
						>
							<img
								src={logo}
								alt="Fantasy Fitness"
								className="h-10 w-10 rounded object-cover flex-shrink-0"
							/>
							<span className="text-lg font-semibold text-gray-900 dark:text-dark-text truncate">
								Fantasy Fitness
							</span>
						</Link>
						<button
							type="button"
							onClick={closeAccordion}
							className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg flex-shrink-0"
							aria-label="Close navigation"
						>
							<i className="fa-solid fa-xmark text-lg" />
						</button>
					</div>

					<nav className="flex-1 overflow-y-auto px-3 py-4">
						{tabs.map((item) => renderNavItem(item))}
					</nav>

					<div className="px-4 py-4 border-t border-gray-200 dark:border-dark-border">
						<div className="flex items-center justify-between gap-3 mb-3">
							{authenticated ? (
								isHouseholdSession ? (
									<div
										className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 flex-1 min-w-0 cursor-default"
										title="Account settings are only available to the account owner"
									>
										<i className="fa-solid fa-circle-user flex-shrink-0" />
										<span className="truncate">{displayName}</span>
									</div>
								) : (
									<button
										type="button"
										onClick={() => handleNavigate('/user-profile')}
										className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-ff-black dark:hover:text-white transition-colors flex-1 min-w-0"
									>
										<i className="fa-solid fa-circle-user flex-shrink-0" />
										<span className="truncate">{displayName}</span>
									</button>
								)
							) : (
								location.pathname !== '/signin' && (
									<button
										type="button"
										onClick={() => handleNavigate('/signin')}
										className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-ff-black dark:hover:text-white transition-colors flex-1 min-w-0"
									>
										<i className="fa-solid fa-arrow-right-to-bracket flex-shrink-0" />
										<span>Sign In</span>
									</button>
								)
							)}
							<ThemeToggle className="flex-shrink-0" />
						</div>
						{authenticated && (
							<button
								type="button"
								onClick={() => {
									closeAccordion();
									signOut();
								}}
								className="flex items-center gap-2 py-2 px-2 -ml-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors w-full"
							>
								<i className="fa-solid fa-arrow-right-from-bracket flex-shrink-0" />
								<span>Sign Out</span>
							</button>
						)}
					</div>
				</div>
			</aside>
		</React.Fragment>
	);
};

export default AccordionNavigation;
